<script setup lang="ts">
import { useCmsTranslations } from "@shopware-pwa/composables-next";
import {
  useAddToCart,
  useNotifications,
  useCartNotification,
  useCartErrorParamsResolver,
} from "#imports";
import { toRefs, computed } from "vue";
import { defu } from "defu";
import type { Schemas } from "#shopware";
import { getCmsTranslate } from "@shopware-pwa/helpers-next";
import { addToCartTracking } from "~/components/utilities/GtmTrackings";

const { pushSuccess, pushError } = useNotifications();
const { getErrorsCodes } = useCartNotification();
const { resolveCartError } = useCartErrorParamsResolver();
const props = defineProps<{
  product: Schemas["Product"];
}>();
const { t } = useI18n();

type Translations = {
  errors: {
    [key: string]: string;
  };
};

let translations: Translations = {
  errors: {
    "product-stock-reached":
      "The product {name} is only available {quantity} times",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { product } = toRefs(props);
const { addToCart, quantity } = useAddToCart(product);

const isAddToCartProcessing = ref(false);
const addToCartProxy = async () => {
  isAddToCartProcessing.value = true;
  await addToCart();
  const errors = getErrorsCodes();
  errors?.forEach((element) => {
    const { messageKey, params } = resolveCartError(element);
    pushError(getCmsTranslate(translations.errors[messageKey], params));
    isAddToCartProcessing.value = false;
  });

  if (!errors.length)
    pushSuccess(
      `${props.product?.translated.name}` +
        " " +
        t("productDetails.messages.addedToCart")
    );
  isAddToCartProcessing.value = false;
  addToCartTracking(product.value, quantity);
};

const minPurchase = computed(() => product.value.minPurchase || 1);
const maxPurchase = computed(() => product.value.calculatedMaxPurchase || 1);

const increaseQuantity = () => {
  if (quantity.value < maxPurchase.value) {
    quantity.value++;
  }
};

const decreaseQuantity = () => {
  if (quantity.value > minPurchase.value) {
    quantity.value--;
  }
};
</script>

<template>
  <div class="add-product flex items-center justify-between mb-6">
    <!-- Quantity Selector -->
    <div class="flex items-center bg-dark-bg rounded-full text-white text-base">
      <!-- Minus Button -->
      <button
        id="decrease-btn"
        class="py-3 px-6 bg-transparent"
        @click="decreaseQuantity"
        :disabled="quantity <= minPurchase"
      >
        <svg
          width="14"
          height="2"
          viewBox="0 0 14 2"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1.25 1.74995C1.0375 1.74995 0.859375 1.67805 0.715625 1.53425C0.571875 1.39043 0.5 1.21223 0.5 0.99965C0.5 0.78705 0.571875 0.608958 0.715625 0.465375C0.859375 0.321791 1.0375 0.25 1.25 0.25H12.75C12.9625 0.25 13.1406 0.3219 13.2843 0.4657C13.4281 0.609516 13.5 0.787717 13.5 1.0003C13.5 1.2129 13.4281 1.39099 13.2843 1.53457C13.1406 1.67816 12.9625 1.74995 12.75 1.74995H1.25Z"
            fill="white"
          />
        </svg>
      </button>
      <!-- Quantity Display -->
      <span id="quantity-display" class="font-bold">{{ quantity }}</span>
      <!-- Plus Button -->
      <button
        id="increase-btn"
        class="py-3 px-6 bg-transparent"
        @click="increaseQuantity"
        :disabled="quantity >= maxPurchase"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.25 7.74995H1.25C1.0375 7.74995 0.859375 7.67805 0.715625 7.53425C0.571875 7.39043 0.5 7.21223 0.5 6.99965C0.5 6.78705 0.571875 6.60896 0.715625 6.46538C0.859375 6.32179 1.0375 6.25 1.25 6.25H6.25V1.25C6.25 1.0375 6.3219 0.859375 6.4657 0.715625C6.60952 0.571875 6.78772 0.5 7.0003 0.5C7.2129 0.5 7.39099 0.571875 7.53457 0.715625C7.67816 0.859375 7.74995 1.0375 7.74995 1.25V6.25H12.75C12.9625 6.25 13.1406 6.3219 13.2843 6.4657C13.4281 6.60952 13.5 6.78772 13.5 7.0003C13.5 7.2129 13.4281 7.39099 13.2843 7.53458C13.1406 7.67816 12.9625 7.74995 12.75 7.74995H7.74995V12.75C7.74995 12.9625 7.67805 13.1406 7.53425 13.2843C7.39043 13.4281 7.21223 13.5 6.99965 13.5C6.78705 13.5 6.60896 13.4281 6.46537 13.2843C6.32179 13.1406 6.25 12.9625 6.25 12.75V7.74995Z"
            fill="white"
          />
        </svg>
      </button>
    </div>
    <input
      id="qty"
      v-model="quantity"
      type="number"
      :min="product.minPurchase || 1"
      :max="product.calculatedMaxPurchase"
      :step="product.purchaseSteps || 1"
      class="hidden"
      data-testid="product-quantity"
    />
    <button
      :disabled="!product.available"
      class="btn-default-px-9 btn-default btn-black-bg flex px-6 md:px-[75px]"
      :class="{
        'opacity-50 cursor-not-allowed':
          !product.available || isAddToCartProcessing,
      }"
      data-testid="add-to-cart-button"
      @click="addToCartProxy"
    >
      {{ $t("productDetails.addToCart") }}

      <span class="ml-2">
        <i
          v-if="isAddToCartProcessing"
          class="fa-solid fa-spinner animate-spin"
        ></i>
        <svg
          v-else
          width="16"
          height="20"
          viewBox="0 0 16 20"
          fill="none"
          xmlns="https://www.w3.org/2000/svg"
        >
          <path
            d="M2.3077 19.5C1.80257 19.5 1.375 19.325 1.025 18.975C0.675 18.625 0.5 18.1974 0.5 17.6923V6.3077C0.5 5.80257 0.675 5.375 1.025 5.025C1.375 4.675 1.80257 4.5 2.3077 4.5H4.25V4.25C4.25 3.2141 4.61602 2.33013 5.34808 1.59808C6.08011 0.866026 6.96408 0.5 7.99997 0.5C9.03588 0.5 9.91984 0.866026 10.6519 1.59808C11.3839 2.33013 11.75 3.2141 11.75 4.25V4.5H13.6922C14.1974 4.5 14.625 4.675 14.975 5.025C15.325 5.375 15.5 5.80257 15.5 6.3077V17.6923C15.5 18.1974 15.325 18.625 14.975 18.975C14.625 19.325 14.1974 19.5 13.6922 19.5H2.3077ZM2.3077 18H13.6922C13.7692 18 13.8397 17.9679 13.9038 17.9038C13.9679 17.8397 14 17.7692 14 17.6923V6.3077C14 6.23077 13.9679 6.16024 13.9038 6.09613C13.8397 6.03203 13.7692 5.99998 13.6922 5.99998H11.75V8.25C11.75 8.46282 11.6782 8.64102 11.5346 8.7846C11.391 8.92818 11.2128 8.99998 11 8.99998C10.7872 8.99998 10.609 8.92818 10.4654 8.7846C10.3218 8.64102 10.25 8.46282 10.25 8.25V5.99998H5.74995V8.25C5.74995 8.46282 5.67816 8.64102 5.53457 8.7846C5.39099 8.92818 5.21279 8.99998 4.99997 8.99998C4.78716 8.99998 4.60896 8.92818 4.46537 8.7846C4.32179 8.64102 4.25 8.46282 4.25 8.25V5.99998H2.3077C2.23077 5.99998 2.16024 6.03203 2.09612 6.09613C2.03202 6.16024 1.99997 6.23077 1.99997 6.3077V17.6923C1.99997 17.7692 2.03202 17.8397 2.09612 17.9038C2.16024 17.9679 2.23077 18 2.3077 18ZM5.74995 4.5H10.25V4.25C10.25 3.62307 10.0317 3.09133 9.59517 2.6548C9.15864 2.21825 8.62691 1.99998 7.99997 1.99998C7.37304 1.99998 6.84131 2.21825 6.40477 2.6548C5.96822 3.09133 5.74995 3.62307 5.74995 4.25V4.5Z"
            fill="white"
          />
        </svg>
      </span>
    </button>
  </div>
</template>
